import { AxiosResponse } from 'axios'

import type { ApiClient } from 'clients/yogi-api'
import type { Role } from 'types/hub-role'
const USER_VISIT_URL = '/user/visit'
const USER_DETAILS_URL = '/user'
const USER_CONTEXT_URL = '/client/hub/user'
const USER_REQUEST_ACCESS_URL = '/user/request-access'
const USER_TRIAL_SIGNUP_URL = '/user/trial-signup'
const USER_RESEND_ACTIVATION_URL = '/user/resend-account-activation'
const HUB_UUID_HEADER = 'X-Hub-UUID'

/**
 * RBAC v2 hub role returned by `GET /client/hub/user` for the active hub.
 * The route returns the full user-details superset; the client reads only the
 * v2 `hub_role` from it and merges it into `userDetails`.
 */
export type UserContextRoles = {
  hub_role: Role
}

export class AuthService {
  constructor(private client: ApiClient) {}

  /**
   * Signs up for free trial
   * @param {string} email
   * @param {string} first_name
   * @param {string} last_name
   * @param {string} org
   * @param {string} team
   * @returns {User} User entity
   */
  freeSignUp(
    email: string,
    first_name: string,
    last_name: string,
    org: string,
    team: string,
  ): Promise<AxiosResponse<void>> {
    return this.client.post(USER_TRIAL_SIGNUP_URL, {
      email,
      first_name,
      last_name,
      org,
      team,
    })
  }

  updateLastVisit(): Promise<AxiosResponse<unknown>> {
    return this.client.put(USER_VISIT_URL)
  }

  fetchUser(): Promise<AxiosResponse<unknown>> {
    return this.client.get(USER_DETAILS_URL)
  }

  /**
   * Fetch the RBAC v2 hub role for the active hub. The hub context is supplied
   * via the `X-Hub-UUID` header (the canonical hub-scoped client-route
   * contract); `hub_role` is resolved for that hub and defaults to `member`
   * server-side when unresolved. Returns the domain shape (no transport-type
   * leak), matching the rule the sibling legacy methods predate.
   */
  async fetchUserContext(hubUuid: string): Promise<UserContextRoles> {
    const { data } = await this.client.get<UserContextRoles>(USER_CONTEXT_URL, {
      headers: { [HUB_UUID_HEADER]: hubUuid },
    })
    return { hub_role: data.hub_role }
  }

  requestAccess(_: string): Promise<AxiosResponse<unknown>> {
    return this.client.get(USER_REQUEST_ACCESS_URL)
  }

  /**
   * Re-send the account-activation email for a user whose temporary password
   * expired. Public + IP rate-limited server-side; always resolves with a
   * fixed acknowledgement regardless of whether the account exists (no
   * account-state leak), so this returns the domain shape (`void`) rather than
   * the transport response.
   */
  async resendAccountActivation(email: string): Promise<void> {
    await this.client.post(USER_RESEND_ACTIVATION_URL, { email })
  }
}
